import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { Barcode, Pencil } from 'lucide-react';
import type { ProductMediaSummary, ProductType, ProductVariantSummary } from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { detailFieldsFor, detailKeyFor } from '../variant-form';
import { BarcodeManager } from './BarcodeManager';
import { PhotoGallery } from './PhotoGallery';
import { ProductThumb } from './ProductThumb';

interface VariantCardProps {
  productId: string;
  productType: ProductType;
  variant: ProductVariantSummary;
  /** This variant's own photographs, in gallery order. */
  photos: ProductMediaSummary[];
  canEdit: boolean;
  onEdit: () => void;
  /** Opens the label builder with this variant; absent when the variant cannot have labels printed. */
  onPrintLabels?: () => void;
  onFeedback: (message: string) => void;
}

/** One variant: price, its typed optical specification, and its barcodes. */
export function VariantCard({
  productId,
  productType,
  variant,
  photos,
  canEdit,
  onEdit,
  onPrintLabels,
  onFeedback,
}: VariantCardProps) {
  const { t } = useTranslation();
  const { format: formatMoney } = useCurrency();

  const detailKey = detailKeyFor(productType);
  const details = detailKey
    ? ((variant as unknown as Record<string, unknown>)[detailKey] as Record<string, unknown> | null)
    : null;

  // Only show specifications that were actually filled in; a wall of dashes helps nobody.
  const populated = details
    ? detailFieldsFor(productType).filter((field) => {
        const value = details[field.key];
        return value !== null && value !== undefined && value !== false && value !== '';
      })
    : [];

  return (
    <div className="border-line rounded-lg border">
      <div className="border-line flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 items-start gap-3">
          <ProductThumb image={variant.image} alt={variant.variantName ?? variant.sku} size="md" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-ink text-sm font-medium">
                {variant.variantName ?? variant.sku}
              </span>
              {!variant.isActive ? <Badge tone="neutral">{t('status.inactive')}</Badge> : null}
            </div>
            <p className="text-muted mt-0.5 font-mono text-xs" dir="ltr">
              {variant.sku}
            </p>
            {variant.image?.source === 'PRODUCT' ? (
              <p className="text-muted mt-0.5 text-xs">{t('catalog.media.usingProductPhoto')}</p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-end">
            <p className="text-ink text-sm font-semibold">
              {formatMoney(variant.sellingPriceMinor)}
            </p>
            {variant.costPriceMinor !== null ? (
              <p className="text-muted text-xs">
                {t('catalog.fields.costPrice')}: {formatMoney(variant.costPriceMinor)}
              </p>
            ) : null}
          </div>

          {onPrintLabels ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrintLabels}
              aria-label={t('catalog.labels.printFor', { sku: variant.sku })}
            >
              <Barcode className="size-4" aria-hidden />
              <span className="hidden sm:inline">{t('catalog.labels.printBarcode')}</span>
            </Button>
          ) : null}

          {canEdit ? (
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="size-4" aria-hidden />
              <span className="hidden sm:inline">{t('common.edit')}</span>
            </Button>
          ) : null}
        </div>
      </div>

      {populated.length > 0 ? (
        <dl className="border-line grid grid-cols-2 gap-3 border-b px-4 py-3 sm:grid-cols-3">
          {populated.map((field) => {
            const value = details?.[field.key];

            return (
              <div key={field.key} className="min-w-0">
                <dt className="text-muted text-xs">{t(field.labelKey)}</dt>
                <dd
                  className="text-ink mt-0.5 truncate text-sm"
                  // Numbers stay LTR; free text infers direction from its own content.
                  dir={field.kind === 'text' ? 'auto' : 'ltr'}
                >
                  {renderDetailValue(field.kind, field.optionLabelPrefix, value, t)}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}

      <div className="border-line border-b px-4 py-3">
        <p className="text-muted mb-2 text-xs font-medium">{t('catalog.media.variantPhotos')}</p>
        <PhotoGallery
          productId={productId}
          variantId={variant.id}
          photos={photos}
          canEdit={canEdit}
          subject={variant.variantName ?? variant.sku}
          onFeedback={onFeedback}
        />
      </div>

      <div className="px-4 py-3">
        <p className="text-muted mb-2 text-xs font-medium">{t('catalog.barcodes.title')}</p>
        <BarcodeManager
          productId={productId}
          variant={variant}
          canEdit={canEdit}
          onFeedback={onFeedback}
        />
      </div>
    </div>
  );
}

function renderDetailValue(
  kind: string,
  optionLabelPrefix: string | undefined,
  value: unknown,
  t: (key: string) => string,
): string {
  if (kind === 'boolean') return value === true ? t('common.yes') : t('common.no');
  if (kind === 'enum' && optionLabelPrefix) return t(`${optionLabelPrefix}.${String(value)}`);
  return String(value);
}
